// Credit note helpers shared by the ONE credit-note door (CreditNoteDrawer + its customer > order picker) and the forms that
// reuse the text-amount / idempotency-key / order-picker rules (no React, no network, no money arithmetic). Moved here from the old customer-only helper module (47204).
// The server is authoritative; these rules only shape the pickers.

import { orderPickerLabel } from './orderPickerLabel.mjs';

export const ORDER_PAGE_SIZE = 200;   // the initial list = the newest 200 orders (the list endpoint's max page); typing asks GET /sales-orders?search= for the rest (W13, R-Q 4)
export const SEARCH_MIN = 2;   // characters typed before the server is asked

// A positive decimal TEXT (no float parse): at least one non-zero digit, no sign.
const isPositiveText = (v) => v != null && /^\d*\.?\d*$/.test(String(v)) && /[1-9]/.test(String(v));

// Which orders a customer credit note can target (§1.3): posted, not a quote, not cancelled, something recognised.
// DONE orders stay eligible — a price adjustment after settlement is legitimate; the supplier form likewise offers
// every posted order (its 'Open only' box is off by default) and its Supplier Credit mode accepts any order.
export const isCreditable = (o) => !!o && !!o.operation_id
  && (o.tx_status == null || o.tx_status === 'posted')
  && !['quote', 'cancelled'].includes(String(o.delivery_status || '').toLowerCase())
  && isPositiveText(o.recognized_value);

// List rows -> unique creditable orders, newest first; the locked/prefilled order is always present (supplier Prompt 214).
export function creditableOrders(rows, pinnedId = '') {
  const seen = new Map();
  for (const r of rows || []) if (isCreditable(r) && !seen.has(r.operation_id)) seen.set(r.operation_id, r);
  const key = (o) => `${o.op_date || ''}|${o.operation_id}`;
  const list = [...seen.values()].sort((a, b) => key(b).localeCompare(key(a)));
  if (pinnedId && !seen.has(pinnedId)) {
    const known = (rows || []).find(r => r.operation_id === pinnedId);
    list.unshift(known || { operation_id: pinnedId });
  }
  return list;
}

// The server already matched the typed text (order id, date, amount) on its own forms; the Combobox's local filter must not hide those rows, so the typed text joins their search text.
export const withTyped = (options, q) => (q ? options.map(o => ({ ...o, search: `${o.label} ${q}` })) : options);

// Combobox options. fmtDate / statusLabel are injected (Settings > Formatting + i18n live in the caller).
export const orderOptions = (orders, { fmtDate, fmtMoney, statusLabel }) => orders.map(o => ({
  value: o.operation_id,
  label: orderPickerLabel(o, { fmtDate, fmtMoney, statusLabel }),   // the ONE picker label shared by every sales door
}));

// User text -> exact 2-dp API text ('' when unreadable). Strips spaces and the configured thousands separator,
// maps the configured decimal separator to '.'. Text in, text out — never a float.
export function normalizeAmount(text, { decimal = ',', thousands = ' ' } = {}) {
  let s = String(text || '').replace(/[\s  ]/g, '');
  if (thousands && thousands !== decimal) s = s.split(thousands).join('');
  if (decimal !== '.') s = s.split(decimal).join('.');
  return /^\d+(\.\d{1,2})?$/.test(s) && /[1-9]/.test(s) ? s : '';
}

// requestRaw result -> what the form shows. A 404/405/501 WITHOUT an API error body is a route that does not exist
// yet (Express's default HTML 404) -> 'unavailable'; a JSON refusal (code/error) is a real answer -> 'refused'.
export function interpretResponse(res) {
  if (!res) return { kind: 'error' };
  const { ok, status, data } = res;
  if (ok) return { kind: 'ok', data: data || {} };
  // The API's catch-all answers an unknown route with 404 { error: 'No such API route: …' } (server.js app.use('/api')).
  const noRoute = !data?.code && (!data?.error || /^No such API route/.test(data.error));
  if ([404, 405, 501].includes(status) && noRoute) return { kind: 'unavailable' };
  if (status >= 400 && status < 500) return { kind: 'refused', code: data?.code || null, message: data?.error || data?.message || '', data: data || {} };
  return { kind: 'error', message: data?.error || '' };
}

// One key per form open / per successful issue (D16): the shared v4 generator (works over plain http too)
export { newKey as newIdempotencyKey } from '../../lib/idempotencyKey.mjs';
